<script setup lang="ts">
import {
  useGetArchivedDrawings,
  useUnarchiveDrawing,
  useDeleteDrawing,
} from "@/stores/queries";
import type { DRDrawing, PaginationParams } from "@/types";
import { computed, ref } from "vue";
import DwgIcon from "../UI/icons/DwgIcon.vue";
import DRBtnIcon from "../UI/DRBtnIcon.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";

const props = withDefaults(
  defineProps<{
    projectId: number;
  }>(),
  {}
);

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 100,
  sortBy: "archivedAt",
  order: "desc",
});

const filters = computed(() => ({
  projectId: props.projectId,
}));

const {
  data: drawings,
  refetch,
  isLoading,
} = useGetArchivedDrawings(pagination, filters);

const { mutateAsync: unarchiveDrawing } = useUnarchiveDrawing();
const { mutateAsync: deleteDrawingMutation, isPending: isDeleting } =
  useDeleteDrawing();

async function unArchive(drawing: DRDrawing) {
  unarchiveDrawing(drawing, {
    onSuccess: () => {
      refetch();
    },
  });
}

const drawingToDelete = ref<DRDrawing | undefined>(undefined);
const showConfirmDelete = computed({
  get: () => !!drawingToDelete.value,
  set: (value: boolean) => {
    if (!value) drawingToDelete.value = undefined;
  },
});

function confirmDelete(drawing: DRDrawing) {
  drawingToDelete.value = drawing;
}

async function deleteDrawing() {
  if (!drawingToDelete.value) return;
  await deleteDrawingMutation(drawingToDelete.value, {
    onSuccess: () => {
      drawingToDelete.value = undefined;
      refetch();
    },
  });
}
</script>

<template>
  <el-dropdown-menu>
    <div class="px-3 py-2 fw-medium" style="width: 10rem">
      {{ $t("archivesList") }}
    </div>
    <template v-if="!isLoading">
      <div
        v-if="drawings?.length"
        class="px-1"
        style="max-height: 200px; overflow-y: auto"
      >
        <div
          :key="drawing.id"
          v-for="drawing in drawings"
          class="hoverable-white rounded py-1 ps-3 pe-1"
        >
          <div class="d-flex align-items-center gap-2">
            <DwgIcon :size="20" />
            <div style="width: 12rem" class="text-truncate">
              {{ drawing.name }}
            </div>
            <DRBtnIcon
              icon="bi-arrow-clockwise"
              variant="flat"
              :size="30"
              @click.prevent="unArchive(drawing)"
            >
            </DRBtnIcon>
            <DRBtnIcon
              icon="bi-trash"
              variant="flat-danger"
              :size="30"
              @click.prevent="confirmDelete(drawing)"
            >
            </DRBtnIcon>
          </div>
        </div>
      </div>
      <div v-else class="px-3 py-2 text-grey">
        {{ $t("dashboard:no.plan") }}
      </div>
    </template>
  </el-dropdown-menu>

  <ConfirmationModal
    v-model="showConfirmDelete"
    danger
    :title="$t('dashboard:drawings.delete.confirmation.title')"
    :message="$t('dashboard:drawings.delete.confirmation.message')"
    :loading="isDeleting"
    @confirm="deleteDrawing"
  />
</template>

<style lang="scss">
.el-dropdown-menu__item {
  height: 2.3rem !important;
}
</style>
